/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { Lzp3 } from "@ledgerhq/compressjs";
import { combineEpics } from "redux-observable";
import { empty, from } from "rxjs";
import { ajax } from "rxjs/ajax";
import { exhaustMap, filter, map, pluck, switchMap } from "rxjs/operators";
import { isActionOf } from "typesafe-actions";
import config from "../../config";
import { getDB } from "../../db/store";
import { createEpic, getCommonHeaders } from '../../utils/epics';
import { EVALS_STORE, EVALS_TO_CREATE_STORE } from "./../../db/stores/evals";
import { LIVE_EVAL_PATCHES_STORE, PENDING_EVAL_PATCHES_STORE } from "./../../db/stores/patches";
import { sendDiagnosticDataActions } from "./actions";

const sendDiagnosticsEpic = createEpic((action$, state$) => {
  return action$.pipe(
    filter(isActionOf(sendDiagnosticDataActions.request)),
    pluck("payload"),
    exhaustMap(userReason => {
      return from(
        getDB().then(db => {
          const storesToSend = [
            EVALS_STORE,
            EVALS_TO_CREATE_STORE,
            LIVE_EVAL_PATCHES_STORE,
            PENDING_EVAL_PATCHES_STORE
          ];
          const tx = db.transaction(storesToSend as any);

          const dbPayload: { [storeName: string]: any } = {};

          return Promise.all(
            storesToSend.map(storename =>
              tx
                .objectStore(storename)
                .getAll()
                .then((contents: any) => {
                  dbPayload[storename] = contents;
                })
            )
          ).then(() => ({
            userReason,
            db: dbPayload,
          }));
        })
      ).pipe(
        map(json => JSON.stringify(json)),
        map(json => new window.TextEncoder().encode(json)),
        map(encoded => Lzp3.compressFile(encoded, undefined, 9)),
        switchMap(compressed => {
          const formData = new FormData();
          const mimeType = 'application';
          formData.append("mime_type", mimeType);
          const blob = new Blob([compressed], {type:mimeType})
          const headers: {[key: string]: any} = getCommonHeaders(state$);
          formData.append("attachment", blob);
          if (userReason){
            formData.append("reason", userReason);
          }

          return ajax.post(`${config.dfdmHost}/api/debug/client_data`, formData,
              headers
            );
        })
      );
    }),
    switchMap(() => empty())
  );
});

export default combineEpics(sendDiagnosticsEpic);
